"use client";

// The Job dashboard — the approved mockup's design, implemented with STATIC
// values (this pass: nothing below is computed from the fetched /jobs or
// /companies data; see STATIC MOCK DATA below for the swap points where real
// values plug in later).
//
// Desktop and mobile render DIFFERENT dashboard content, per the approved
// mockups — the mobile spec is not a reflow of the desktop one. They are two
// compositions over the same page: the desktop markup hides below `lg`, the
// mobile markup hides at and above it. (The shell's own chrome already
// switches at the same breakpoint.)
//
// Chart colors are the mockup's literal hexes: the token system's chart series
// (--color-chart-1..4) do not match the approved picks (blue #1b66f6, purple
// #8e6bf3, amber #f6ae41, green #2dd57e), and the brief pins those exact
// values. The neutral surfaces stay on tokens, so the dark-theme convergence
// with the landing page still flows through unchanged.

import type { ComponentType } from "react";
import Link from "next/link";
import { StatCard } from "@/app/components/stat-card";
import { ArcDonut } from "@/app/components/arc-donut";
import { FunnelRow, PipelineRow } from "@/app/components/progress-rows";
import { LegendChip } from "@/app/components/chart-bits";
import { BuildingIcon, CalendarIcon, ChevronDownIcon, GlobeIcon } from "@/app/components/icons";
import { useAuth } from "@/app/lib/auth-context";
import { usePrefersReducedMotion } from "@/app/lib/use-reduced-motion";
import { Landing } from "@/app/components/landing";
import { Card } from "@/app/components/ui/card";

// ── STATIC MOCK DATA ────────────────────────────────────────────────────────
// Every number below is from the approved mockup, hard-coded per the brief.
// When real data plugs in: DESKTOP_STATS / DESKTOP_PIPELINE / DESKTOP_DONUT /
// DESKTOP_CHART / DESKTOP_FUNNEL come from the /jobs + /companies fetches
// (deltas need a comparison window), and the MOBILE_* constants from an
// applications endpoint that does not exist yet.
const DESKTOP_STATS = [
  { label: "Total jobs", value: 8, delta: 1, tone: "blue" as const, deltaTone: "green" as const, icon: GlobeIcon },
  { label: "Companies", value: 9, delta: 2, tone: "amber" as const, deltaTone: "amber" as const, icon: BuildingIcon },
  { label: "Analyzed", value: 7, delta: 0, tone: "neutral" as const, deltaTone: "green" as const, icon: CalendarIcon },
] satisfies DesktopStat[];

interface DesktopStat {
  label: string;
  value: number;
  delta: number;
  tone: "blue" | "amber" | "neutral";
  deltaTone: "green" | "amber";
  icon: ComponentType<{ className?: string }>;
}

// The mockup's four series colors — exact hexes, both themes (the mockup's
// light panels carry the same data colors; only surfaces invert).
const MOCK_BLUE = "#1b66f6";
const MOCK_PURPLE = "#8e6bf3";
const MOCK_AMBER = "#f6ae41";
const MOCK_GREEN = "#2dd57e";

const DESKTOP_PIPELINE = [
  { label: "Remote", pct: 38, value: "38%", color: MOCK_BLUE },
  { label: "Hybrid", pct: 25, value: "25%", color: MOCK_PURPLE },
  { label: "Onsite", pct: 18, value: "18%", color: MOCK_AMBER },
  { label: "Analyzed", pct: 88, value: "88%", color: MOCK_GREEN },
];

const DESKTOP_DONUT = [
  { label: "Remote", value: 38, color: MOCK_BLUE },
  { label: "Hybrid", value: 25, color: MOCK_PURPLE },
  { label: "Onsite", value: 18, color: MOCK_AMBER },
  { label: "Other", value: 19, color: "var(--color-muted-foreground)" },
];
const DESKTOP_TOTAL_JOBS = 8;

const DESKTOP_BARS = [3, 5, 2, 6, 4, 2, 5, 3, 1, 4, 2, 5, 3];
const DESKTOP_TICKS = [2, 4, 6];
const DESKTOP_BAR_LABELS: Record<number, string> = {
  0: "Apr 28",
  3: "May 1",
  6: "May 4",
  9: "May 7",
  12: "May 10",
};
const DESKTOP_CHART_LEGEND = [
  { label: "Jobs discovered", color: MOCK_BLUE },
];

// The approved mockup's four-step blue ramp — exact hexes, darkest first.
const FUNNEL_RAMP = ["#1a59bc", "#4983e2", "#80a7eb", "#aac0f1"];
const DESKTOP_FUNNEL = [
  { label: "Applied", count: 8 },
  { label: "Response received", count: 5 },
  { label: "Interview", count: 3 },
  { label: "Offer/Rejected", count: 2 },
].map((stage, index) => ({ ...stage, color: FUNNEL_RAMP[index] }));

const MOBILE_STATS = [
  { label: "Total Jobs", value: 24, tone: "neutral" as const },
  { label: "In Progress", value: 8, tone: "blue" as const },
  { label: "Interviews", value: 3, tone: "blue" as const },
];

const MOBILE_STATUS = [
  { label: "Applied", value: 10, color: MOCK_BLUE },
  { label: "Interview", value: 8, color: MOCK_PURPLE },
  { label: "Offer", value: 2, color: MOCK_GREEN },
  { label: "Rejected", value: 4, color: "var(--destructive)" },
];
const MOBILE_TOTAL = 24;

const MOBILE_BARS = [2, 4, 1, 5, 3, 6, 2, 4, 5, 1, 3, 6, 2, 4, 5, 1, 6, 3, 2, 5, 4, 1, 3, 6, 2, 5, 3, 4];
const MOBILE_TICKS = [2, 4, 6];
const MOBILE_BAR_LABELS: Record<number, string> = {
  0: "Apr 1",
  7: "Apr 7",
  14: "Apr 14",
  21: "Apr 21",
  27: "Apr 28",
};
const MOBILE_CHART_LEGEND = [
  { label: "Jobs per day", color: MOCK_BLUE },
];

// Recent matches — STATIC placeholder rows (the landing hero mockup's own
// values), styled after the landing's RecentMatches panel. NEEDS REAL DATA
// WIRING: the Jobs page reads match scores from useJobMatches() →
// getMyMatches() (Record<job_id, MatchResult> with .match_score), but that
// type carries no "matched at" timestamp and this page no longer fetches
// /jobs — real rows need the matches endpoint joined to /jobs (recency could
// proxy off job.discovered_at) before these literals can go away.
const RECENT_MATCHES = [
  {
    initial: "S",
    bg: "#0066f9",
    darkText: false,
    role: "Supply Chain Manager",
    org: "BMW Group · Munich, Germany",
    pct: 94,
    time: "2 hours ago",
  },
  {
    initial: "DB",
    bg: "#e2001a",
    darkText: false,
    role: "Logistics Project Manager",
    org: "Deutsche Bahn · Berlin, Germany",
    pct: 87,
    time: "5 hours ago",
  },
  {
    initial: "DHL",
    bg: "#ffcc00",
    darkText: true,
    role: "Operations Planner",
    org: "DHL · Leipzig, Germany",
    pct: 82,
    time: "1 day ago",
  },
];

export default function Home() {
  const { token, loading: authLoading } = useAuth();
  const reduced = usePrefersReducedMotion();

  // Auth beats preserved from the previous build: nothing renders while auth
  // resolves, and signed-out visitors get the marketing landing.
  if (authLoading) {
    return null;
  }

  if (!token) {
    return <Landing />;
  }

  return (
    <>
      <DesktopDashboard reduced={reduced} />
      <MobileDashboard />
    </>
  );
}

/* ── Dashboard bar chart ─────────────────────────────────────────────────── */

// The approved mockup's "jobs per day" chart: single-series thin bars with
// horizontal gridlines at 2/4/6, x labels every third bar. The shared
// DiscoveredPerDayChart is the tracker's stacked-by-work-type chart (no
// gridlines, hover readout) — a different device, so this local one mirrors
// the Landing's DiscoveredChart instead.
function DashboardBarChart({
  bars,
  ticks,
  labels,
  scaleMax,
  color,
}: {
  bars: number[];
  ticks: number[];
  labels: Record<number, string>;
  scaleMax: number;
  color: string;
}) {
  return (
    <div>
      <div className="flex h-36 gap-2">
        <div className="flex flex-col justify-between pb-5 text-right text-[10px] tabular-nums text-muted-foreground">
          {[...ticks].reverse().map((tick) => (
            <span key={tick}>{tick}</span>
          ))}
          <span>0</span>
        </div>
        <div className="relative flex-1">
          <div aria-hidden="true" className="absolute inset-0">
            {ticks.map((tick) => (
              <div
                key={tick}
                className="absolute inset-x-0 border-t border-border"
                style={{ bottom: `${(tick / scaleMax) * 100}%` }}
              />
            ))}
          </div>
          <div className="absolute inset-0 flex items-end justify-between gap-[3px]">
            {bars.map((v, i) => (
              <div
                key={i}
                className="w-full rounded-t-[2px]"
                style={{ height: `${(v / scaleMax) * 100}%`, background: color }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="mt-2 flex justify-between pl-6 text-[10px] tabular-nums text-muted-foreground">
        {bars.map((_, i) =>
          labels[i] ? <span key={i}>{labels[i]}</span> : <span key={i} />
        )}
      </div>
    </div>
  );
}

/* ── Desktop ─────────────────────────────────────────────────────────────── */

function DesktopDashboard({ reduced }: { reduced: boolean }) {
  return (
    <main className="mx-auto hidden max-w-7xl px-4 py-8 sm:px-6 lg:block lg:px-8">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">
          Job dashboard
        </h1>
        {/* Static strip — 8 jobs / 9 companies / 7 analyzed / last import 2d ago,
            verbatim from the approved mockup. */}
        <p className="mt-1.5 text-[13px] text-muted-foreground tabular-nums">
          8 jobs · 9 companies · 7 analyzed · last import 2d ago
        </p>
      </div>

      {/* Stat row — the mockup's default-variant cards: green delta on Total
          jobs, amber on Companies, the em-dash "no change" on Analyzed. */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        {DESKTOP_STATS.map((stat, index) => (
          <StatCard
            key={stat.label}
            index={index}
            label={stat.label}
            value={stat.value}
            delta={stat.delta}
            deltaTone={stat.deltaTone}
            icon={stat.icon}
            tone={stat.tone}
          />
        ))}
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <Card className="flex flex-col p-4">
          <h2 className="text-sm font-semibold text-foreground">Pipeline</h2>
          {/* The rows (not the title) center in the space below the heading,
              balancing the taller donut card beside it. */}
          <div className="mt-2 flex flex-1 flex-col justify-center">
            {DESKTOP_PIPELINE.map((row, index) => (
              <PipelineRow
                key={row.label}
                index={index}
                label={row.label}
                pct={row.pct}
                value={row.value}
                color={row.color}
                reduced={reduced}
              />
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <h2 className="text-sm font-semibold text-foreground">Work type distribution</h2>
          <div className="mt-3 flex justify-center">
            <ArcDonut
              data={DESKTOP_DONUT}
              centerValue={DESKTOP_TOTAL_JOBS}
              centerLabel="jobs"
            />
          </div>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {DESKTOP_DONUT.map((segment) => (
              <LegendChip key={segment.label} label={`${segment.label} ${segment.value}%`} color={segment.color} />
            ))}
          </div>
        </Card>
      </div>

      <div className="mb-4">
        <Card className="p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h2 className="text-sm font-semibold text-foreground">Jobs discovered per day</h2>
            <span className="text-xs text-muted-foreground">last 14 days</span>
          </div>
          <div className="mt-3">
            <DashboardBarChart
              bars={DESKTOP_BARS}
              ticks={DESKTOP_TICKS}
              labels={DESKTOP_BAR_LABELS}
              scaleMax={8}
              color={MOCK_BLUE}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {DESKTOP_CHART_LEGEND.map((entry) => (
              <LegendChip key={entry.label} label={entry.label} color={entry.color} />
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-4">
        <h2 className="text-sm font-semibold text-foreground">Application funnel</h2>
        <div className="mt-1 flex flex-col">
          {DESKTOP_FUNNEL.map((stage, index) => (
            <FunnelRow
              key={stage.label}
              index={index}
              label={stage.label}
              count={stage.count}
              pct={(stage.count / DESKTOP_FUNNEL[0].count) * 100}
              color={stage.color}
              reduced={reduced}
            />
          ))}
        </div>
      </Card>

      {/* Recent matches — the landing hero's panel, on the app's own tokens.
          STATIC rows; see the note on RECENT_MATCHES for the wiring gap. */}
      <Card className="mt-4 p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground">Recent matches</h2>
          <Link
            href="/jobs"
            className="text-[12px] font-semibold text-primary transition-colors duration-150 hover:text-primary-hover"
          >
            View all →
          </Link>
        </div>
        <ul className="flex flex-col">
          {RECENT_MATCHES.map((match) => (
            <li
              key={match.role}
              className="flex items-center gap-3 border-t border-border py-2.5 first:border-t-0 first:pt-0 last:pb-0"
            >
              <span
                aria-hidden="true"
                style={{ backgroundColor: match.bg }}
                className={`flex h-9 w-9 flex-none items-center justify-center rounded-button text-[11px] font-bold ${
                  match.darkText ? "text-[#1f2937]" : "text-white"
                }`}
              >
                {match.initial}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-semibold text-foreground">
                  {match.role}
                </span>
                <span className="block truncate text-[12px] text-muted-foreground">{match.org}</span>
              </span>
              <span className="ml-auto flex flex-none items-center gap-3">
                <span className="rounded-pill bg-success/10 px-2 py-0.5 text-[11px] font-bold text-success-text tabular-nums">
                  {match.pct}%
                </span>
                <span className="hidden w-20 text-right text-[12px] text-muted-foreground xl:block">
                  {match.time}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </main>
  );
}

/* ── Mobile ──────────────────────────────────────────────────────────────── */

function MobileDashboard() {
  return (
    <main className="mx-auto max-w-7xl px-3 py-4 pb-8 sm:px-6 lg:hidden">
      <header className="mb-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">
          Job dashboard
        </h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Here&apos;s your progress at a glance.
        </p>
        {/* Static range pill — the mockup's "Last 30 days" dropdown. Non-
            functional this pass; swaps for a Select when ranges are real. */}
        <button
          type="button"
          className="mt-3 inline-flex items-center gap-1.5 rounded-pill border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors duration-150 hover:bg-muted"
        >
          Last 30 days
          <ChevronDownIcon className="h-3.5 w-3.5" />
        </button>
      </header>

      {/* Compact stat cards — the mockup's 24/8/3 row: plain cards, with the
          blue accent on the In Progress / Interviews figures. Numbers render
          as literal markup (not StatCard) to keep the mobile sizing exact. */}
      <div className="mb-4 grid grid-cols-3 gap-2">
        {MOBILE_STATS.map((stat) => (
          <div key={stat.label} className="rounded-card border border-border bg-card p-3">
            <p
              className={
                stat.tone === "blue"
                  ? "text-xl font-bold leading-none tracking-tight text-primary tabular-nums"
                  : "text-xl font-bold leading-none tracking-tight text-foreground tabular-nums"
              }
            >
              {stat.value}
            </p>
            <p className="mt-1.5 text-[11px] font-medium text-muted-foreground">{stat.label}</p>
          </div>
        ))}
      </div>

      <Card className="mb-4 p-4">
        <h2 className="text-sm font-semibold text-foreground">Application status</h2>
        {/* Donut left, legend right — the mockup's side-by-side layout.
            flex-wrap: the 176px donut plus unwrappable legend labels edge past
            320px viewports, so the legend drops below instead of overflowing. */}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-4">
          <div className="scale-90">
            <ArcDonut data={MOBILE_STATUS} centerValue={MOBILE_TOTAL} centerLabel="Total" />
          </div>
          <ul className="flex flex-col gap-2">
            {MOBILE_STATUS.map((segment) => (
              <li key={segment.label} className="flex items-center gap-2 text-xs">
                <span
                  aria-hidden="true"
                  className="h-2 w-2 flex-none rounded-full"
                  style={{ background: segment.color }}
                />
                <span className="text-foreground">{segment.label}</span>
                <span className="ml-auto pl-2 font-semibold text-foreground tabular-nums">
                  {segment.value}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <Card className="mb-4 p-4">
        <h2 className="text-sm font-semibold text-foreground">Jobs per day</h2>
        <div className="mt-3">
          <DashboardBarChart
            bars={MOBILE_BARS}
            ticks={MOBILE_TICKS}
            labels={MOBILE_BAR_LABELS}
            scaleMax={8}
            color={MOCK_BLUE}
          />
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {MOBILE_CHART_LEGEND.map((entry) => (
            <LegendChip key={entry.label} label={entry.label} color={entry.color} />
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="text-sm font-semibold text-foreground">Upcoming activity</h2>
        {/* Static row — "Interview at Stripe / Tomorrow, 10:00 AM", verbatim
            from the approved mockup. */}
        <div className="mt-3 flex items-center gap-3 rounded-card border border-border bg-muted/40 p-3">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-tile-blue-bg text-tile-blue-fg">
            <CalendarIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">Interview at Stripe</p>
            <p className="text-xs text-muted-foreground">Tomorrow, 10:00 AM</p>
          </div>
        </div>
      </Card>
    </main>
  );
}
